/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import {
    IModuleProps, 
    INodeProps,
    Modal,
    ModalBody, 
    ModalHeader
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import * as React from 'react';

import { ICittaCommercialHeaderData } from './citta-commercial-header.data';
import { ICittaCommercialHeaderProps } from './citta-commercial-header.props.autogenerated';

import { NavIcon, Logo } from './components';

export interface IHeaderState {
    mobileMenuCollapsed: boolean;
    signinPopoverOpen: boolean;
}

export interface IHeaderViewProps extends ICittaCommercialHeaderProps<ICittaCommercialHeaderData> {
    HeaderTag: IModuleProps;
    loginLink?: React.ReactNode;
    menuBar?: React.ReactNode[];
    navIcon: React.ReactNode;
    MobileMenuContainer: INodeProps;
    MobileMenuHeader: React.ReactNode;
    MobileMenuBodyContainer: INodeProps;
    logo: React.ReactNode;
}

/**
 *
 * Header component.
 * @extends {React.PureComponent<ICittaCommercialHeaderProps<ICittaCommercialHeaderData>>}
 */
@observer
class CommercialHeader extends React.PureComponent<ICittaCommercialHeaderProps<ICittaCommercialHeaderData>, IHeaderState> {
    constructor(props: ICittaCommercialHeaderProps<ICittaCommercialHeaderData>) {
        super(props);
        this.state = {
            mobileMenuCollapsed: true,
            signinPopoverOpen: false
        };

        this._toggleNavbar = this._toggleNavbar.bind(this);
    }
    
    public componentDidMount(): void {
        this._stickyHeaderSetup();
    }

    public render(): JSX.Element | null {

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- app config is generic
        const menuBarSlot = this._getSlot('menuBar');
        const navbarKey = 'header-nav-mobile';
        const headerClassName = classnames('commercial-header', this.props.config.className);
       
        const {logoLink, logoImage } = this.props.config;

        const viewProps: IHeaderViewProps = {
            ...(this.props as ICittaCommercialHeaderProps<ICittaCommercialHeaderData>),
            ...(this.state),
            logo: <Logo {...{ link: logoLink, image: logoImage, className: 'ms-header-commercial__logo', gridSettings: this.props.context.request.gridSettings, requestContext: this.props.context.request }} />,

            HeaderTag: {
                moduleProps: this.props,
                className: classnames(headerClassName),
                tag: 'header'
            },
            menuBar: menuBarSlot && menuBarSlot.length && menuBarSlot || [],
            navIcon: <NavIcon {...{ mobileHamburgerAriaLabel: 'mobileHamburgerAriaLabel', isExpanded: !this.state.mobileMenuCollapsed, targetId: navbarKey, toggleNavBar: this._toggleNavbar }} />,
            // loginLink: this._getLoginLink(loginLink, loginLinkText),
            MobileMenuContainer: {
                tag: Modal,
                id: navbarKey,
                className: 'ms-header__mobile-hamburger',
                isOpen: !this.state.mobileMenuCollapsed,
                wrapClassName: `ms-header__citta-modal citta-header-mobile ${!this.state.mobileMenuCollapsed ? 'modal-open' : ''}`,
                toggle: this._toggleNavbar
            },
            MobileMenuHeader: (
                <ModalHeader
                    className='ms-header__mobile-hamburger-menu-header'
                    toggle={this._toggleNavbar}
                />
            ),
            MobileMenuBodyContainer: {
                tag: ModalBody,
                className: 'ms-header__mobile-hamburger-menu-body',
            }
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _toggleNavbar(): void {
        console.log('set state', !this.state.mobileMenuCollapsed)
        this.setState({
            mobileMenuCollapsed: !this.state.mobileMenuCollapsed
        });
        this.state.mobileMenuCollapsed && this._setHeader(this.state.mobileMenuCollapsed);
    }

    private _getSlot(slotName: string): React.ReactNode[] | null {
        const { slots } = this.props;
        return (slots && slots[slotName] && slots[slotName].length && slots[slotName]) || null;
    }

    private _setHeader(lockHeader?: boolean): void {
       const headerElem = document.querySelector('.commercial-header');
       if (headerElem) {
           if (lockHeader || document.documentElement.scrollTop > 0) {
               headerElem.classList.add('lock-opaque');
           } else if (!window.document.body.classList.contains('search-open')) {  
               headerElem.classList.remove('lock-opaque');
           }
       }
   }

    private _updateHeader(isInMobileMenu?: boolean): void {
        this._setHeader();
    }
    
    private _stickyHeaderSetup(): void {
        window.onscroll = () => {
            this._updateHeader();
        };
        this._updateHeader();
    }
}

export default CommercialHeader;
